export const formatBytes = (bytes?: number, decimals: number = 2): string => {
  if (!bytes || bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

/**
 * 문자열 전체가 유한 실수 하나로만 해석될 때만 그 숫자를 반환합니다.
 * `parseFloat("100px")`, `parseFloat("2024-05-20")`처럼 앞부분만 잘라 숫자로 쓰는 것을 피합니다.
 * 공백만 있는 문자열은 `null`(원문 유지)입니다.
 */
export const parseFiniteNumberString = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
};

export const formatMetricValue = (x: number): string => {
  if (x === 0) return '0';

  const isNegative = x < 0;
  const absX = Math.abs(x);

  let formatted = '';

  if (absX >= 1000) {
    formatted = Math.floor(absX).toLocaleString();
  } else if (absX >= 100) {
    formatted = Math.floor(absX).toString();
  } else if (absX >= 10) {
    formatted = absX % 1 === 0 ? absX.toString() : (Math.floor(absX * 10) / 10.0).toString();
  } else if (absX >= 0.1) {
    formatted = absX % 1 === 0 ? absX.toString() : (Math.floor(absX * 100) / 100.0).toString();
  } else if (absX >= 0.001) {
    formatted = absX % 1 === 0 ? absX.toString() : (Math.floor(absX * 1000) / 1000.0).toString();
  } else {
    formatted = '0';
  }

  return isNegative && formatted !== '0' ? `-${formatted}` : formatted;
};

export const convertPercentageToFixed = (percentage: string): string => {
  const numericValue = parseFloat(percentage.replace('%', ''));
  return `${formatMetricValue(numericValue)}%`;
};

export const calPercent = (value: number, total: number): string => {
  if (Number.isNaN(value) || Number.isNaN(total)) return '0';
  if (total === 0) return '0';

  return formatMetricValue((value / total) * 100);
};

/**
 * 숫자를 k 단위로 포맷팅 (1000 이상일 때)
 * @param value 포맷팅할 숫자
 * @returns 포맷팅된 문자열 (예: "1.5k" 또는 "999")
 */
export const formatNumberWithK = (value: number): string => {
  if (value >= 1000) {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1,
    })
      .format(value)
      .toLowerCase();
  }
  return value.toString();
};

/**
 * 비율을 버림 방식으로 포맷팅 (TCY-824)
 * @param value 비율 값 (0~100)
 * @returns 포맷팅된 문자열
 * @example
 * formatPercentageWithFloor(12.78) // "12.7"
 * formatPercentageWithFloor(0.856) // "0.85"
 * formatPercentageWithFloor(0.001) // "0.00"
 */
export const formatPercentageWithFloor = (value: number): string => {
  if (value === 0) return '0';

  const absValue = Math.abs(value);

  if (absValue >= 1) {
    return (Math.floor(absValue * 10) / 10).toFixed(1);
  } else {
    return (Math.floor(absValue * 100) / 100).toFixed(2);
  }
};
